import React, { useState } from 'react';
import { BackButton, Button, Input, ErrorMessage, SuccessMessage } from '../ui';
import DiceBackground from '../DiceBackground';
import { MIN_PASSWORD_LENGTH, MAX_USERNAME_LENGTH } from '../../constants';
import { useTranslation } from '../../i18n';

type AuthTab = 'signin' | 'signup' | 'forgot';

interface AuthProps {
  onBack: () => void;
  onSignUp: (data: { email: string; password: string; username: string }) => Promise<void>;
  onSignIn: (data: { email: string; password: string }) => Promise<void>;
  onResetPassword: (email: string) => Promise<void>;
  defaultUsername?: string;
}

export const Auth: React.FC<AuthProps> = ({
  onBack,
  onSignUp,
  onSignIn,
  onResetPassword,
  defaultUsername = '',
}) => {
  const t = useTranslation();
  const [tab, setTab] = useState<AuthTab>('signup');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Sign Up fields
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpUsername, setSignUpUsername] = useState(defaultUsername);
  const [signUpPassword, setSignUpPassword] = useState('');

  // Sign In fields
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');

  // Forgot Password field
  const [forgotEmail, setForgotEmail] = useState('');

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUpEmail.trim() || !signUpPassword.trim() || !signUpUsername.trim()) {
      setError(t.allFieldsRequired);
      return;
    }
    if (signUpPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t.passwordMinLength(MIN_PASSWORD_LENGTH));
      return;
    }
    if (signUpUsername.length > MAX_USERNAME_LENGTH) {
      setError(t.usernameMaxLength(MAX_USERNAME_LENGTH));
      return;
    }

    setIsLoading(true);
    clearMessages();
    try {
      await onSignUp({
        email: signUpEmail.trim(),
        password: signUpPassword,
        username: signUpUsername.trim().toUpperCase(),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.signUpFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signInEmail.trim() || !signInPassword.trim()) {
      setError(t.emailPasswordRequired);
      return;
    }

    setIsLoading(true);
    clearMessages();
    try {
      await onSignIn({
        email: signInEmail.trim(),
        password: signInPassword,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.signInFailed);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setError(t.enterEmail);
      return;
    }

    setIsLoading(true);
    clearMessages();
    try {
      await onResetPassword(forgotEmail.trim());
      setSuccess(t.resetEmailSent);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t.failedResetEmail);
    } finally {
      setIsLoading(false);
    }
  };

  const switchTab = (newTab: AuthTab) => {
    setTab(newTab);
    clearMessages();
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-start text-white p-4 pt-8 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={12} />

      <BackButton onClick={onBack} />

      {/* Title */}
      <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-6 text-theme drop-shadow-lg uppercase text-center relative z-10 mt-8">
        {tab === 'signup' ? t.createAccountTitle : tab === 'signin' ? t.signInTitle : t.resetPasswordTitle}
      </h1>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-6 relative z-10 w-full max-w-xs lg:max-w-md">
        <button
          onClick={() => switchTab('signup')}
          className={`flex-1 p-2 lg:p-3 text-[8px] lg:text-xs font-bold uppercase tracking-widest transition-all pixel-border ${
            tab === 'signup'
              ? 'bg-theme/40 text-theme'
              : 'bg-black/40 text-gray-500 hover:text-gray-300'
          }`}
        >
          {t.signUp}
        </button>
        <button
          onClick={() => switchTab('signin')}
          className={`flex-1 p-2 lg:p-3 text-[8px] lg:text-xs font-bold uppercase tracking-widest transition-all pixel-border ${
            tab === 'signin'
              ? 'bg-theme/40 text-theme'
              : 'bg-black/40 text-gray-500 hover:text-gray-300'
          }`}
        >
          {t.signIn}
        </button>
        <button
          onClick={() => switchTab('forgot')}
          className={`flex-1 p-2 lg:p-3 text-[8px] lg:text-xs font-bold uppercase tracking-widest transition-all pixel-border ${
            tab === 'forgot'
              ? 'bg-theme/40 text-theme'
              : 'bg-black/40 text-gray-500 hover:text-gray-300'
          }`}
        >
          {t.forgotPw}
        </button>
      </div>

      {/* Messages */}
      <div className="w-full max-w-xs lg:max-w-md relative z-10">
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
      </div>

      {/* Sign Up Form */}
      {tab === 'signup' && (
        <form onSubmit={handleSignUp} className="w-full max-w-xs lg:max-w-md flex flex-col gap-4 relative z-10">
          <section className="flex flex-col gap-2">
            <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
              {t.email}
            </label>
            <Input
              type="email"
              value={signUpEmail}
              onChange={(e) => setSignUpEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
              disabled={isLoading}
            />
          </section>

          <section className="flex flex-col gap-2">
            <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
              {t.username}
            </label>
            <Input
              type="text"
              maxLength={MAX_USERNAME_LENGTH}
              value={signUpUsername}
              onChange={(e) => setSignUpUsername(e.target.value.toUpperCase())}
              placeholder={t.warriorNamePlaceholder}
              autoComplete="username"
              disabled={isLoading}
            />
            <span className="text-[8px] lg:text-[9px] text-gray-400 text-center">
              {t.characters(signUpUsername.length, MAX_USERNAME_LENGTH)}
            </span>
          </section>

          <section className="flex flex-col gap-2">
            <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
              {t.password}
            </label>
            <Input
              type="password"
              value={signUpPassword}
              onChange={(e) => setSignUpPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="new-password"
              disabled={isLoading}
              className="!normal-case !tracking-normal"
            />
            <span className="text-[8px] lg:text-[9px] text-gray-400 text-center">
              {t.minimumChars(MIN_PASSWORD_LENGTH)}
            </span>
          </section>

          <Button type="submit" variant="success" size="lg" disabled={isLoading}>
            {isLoading ? t.creating : t.createAccount}
          </Button>

          <p className="text-[8px] lg:text-[9px] text-gray-400 text-center">
            {t.alreadyHaveAccount}{' '}
            <button type="button" onClick={() => switchTab('signin')} className="text-theme hover:underline">
              {t.signIn}
            </button>
          </p>
        </form>
      )}

      {/* Sign In Form */}
      {tab === 'signin' && (
        <form onSubmit={handleSignIn} className="w-full max-w-xs lg:max-w-md flex flex-col gap-4 relative z-10">
          <section className="flex flex-col gap-2">
            <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
              {t.email}
            </label>
            <Input
              type="email"
              value={signInEmail}
              onChange={(e) => setSignInEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
              disabled={isLoading}
            />
          </section>

          <section className="flex flex-col gap-2">
            <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
              {t.password}
            </label>
            <Input
              type="password"
              value={signInPassword}
              onChange={(e) => setSignInPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
              disabled={isLoading}
              className="!normal-case !tracking-normal"
            />
          </section>

          <Button type="submit" variant="primary" size="lg" disabled={isLoading}>
            {isLoading ? t.signingIn : t.signIn}
          </Button>

          <p className="text-[8px] lg:text-[9px] text-gray-400 text-center">
            {t.forgotPassword}{' '}
            <button type="button" onClick={() => switchTab('forgot')} className="text-theme hover:underline">
              {t.resetIt}
            </button>
          </p>
          <p className="text-[8px] lg:text-[9px] text-gray-400 text-center">
            {t.noAccount}{' '}
            <button type="button" onClick={() => switchTab('signup')} className="text-theme hover:underline">
              {t.signUp}
            </button>
          </p>
        </form>
      )}

      {/* Forgot Password Form */}
      {tab === 'forgot' && (
        <form onSubmit={handleForgotPassword} className="w-full max-w-xs lg:max-w-md flex flex-col gap-4 relative z-10">
          <p className="text-[8px] lg:text-xs text-gray-400 text-center mb-2">
            {t.enterEmailReset}
          </p>

          <section className="flex flex-col gap-2">
            <label className="text-[8px] lg:text-[10px] text-gray-500 uppercase tracking-widest text-center">
              {t.email}
            </label>
            <Input
              type="email"
              value={forgotEmail}
              onChange={(e) => setForgotEmail(e.target.value)}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
              disabled={isLoading}
            />
          </section>

          <Button type="submit" variant="warning" size="lg" disabled={isLoading}>
            {isLoading ? t.sending : t.sendResetLink}
          </Button>

          <p className="text-[8px] lg:text-[9px] text-gray-400 text-center">
            {t.rememberedPassword}{' '}
            <button type="button" onClick={() => switchTab('signin')} className="text-theme hover:underline">
              {t.signIn}
            </button>
          </p>
        </form>
      )}
    </div>
  );
};
